import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ReferenceLine, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { convertSingleValue } from '../../utils/commonUtils'

// カスタムラベルコンポーネント
const CustomLabel = ({ x, y, width, value, unit }) => {
    const convertedValue = convertSingleValue(value);  // 数値を変換
    const offset = value >= 0 ? -10 : 20; 
    return (
        <text
            x={x + width / 2} // ラベルを少し右にずらす
            y={y + offset} // ラベルを棒の上に表示
            fontSize={10}
            fill="#000"
            textAnchor="middle"
        >
            {convertedValue}{unit}  {/* 変換した数値を表示 */}
        </text>
    );
};

const CustomTooltip = ({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        const colorMap = {
            prevYear: 'lightgrey',
            latestYear: 'rgba(45, 186, 77, .5)',
        };

        const value = convertSingleValue(payload[0].value); // 数値を変換
        const barFill = payload[0].payload.fill; // 棒の fill (url(#prevYear) など) を取得
        const colorKey = barFill.includes('prevYear') ? 'prevYear' : 'latestYear'; // fill の識別子を取得
        const tooltipColor = colorMap[colorKey]; // 対応する色をマップから取得
        
        return (
            <div className="custom-tooltip" style={{ backgroundColor: tooltipColor, padding: '5px', color: 'white'}}>
                <p className="label">{`${label} : ${value}${unit}`}</p>
            </div>
        );
    }
    return null;
};

const range = (y) => {
    const yMax = Math.max(...y);
    const yMin = Math.min(...y);
    const range = yMax - yMin
    if (yMin > 0) {
        const rangeMax = yMax * 1.2
        const rangeMin = 0;
        return [rangeMin, rangeMax]
    } else if (yMax < 0) {
        const rangeMax = 0
        const rangeMin = yMin * 1.4;
        return [rangeMin, rangeMax]
    } else {
        const rangeMax = yMax + range * 0.2
        const rangeMin = yMin - range * 0.4
        return [rangeMin, rangeMax]
    }
}


const ComparingGraph = ({ financialData, title, column, selectedPeriod, className="dashboard-graph-container" }) => {
    // 最新年度のデータを取得
    const currentYearData = financialData[financialData.length - 1];
    const previousYearData = financialData.length > 1 ? financialData[financialData.length - 2] : null; // 前年のデータがあるか確認
    let data;

    if (selectedPeriod === 'annual') {
        // 前年度のデータがある場合
        if (previousYearData) {
            data = [
                {fiscal_year: previousYearData.fiscal_year + '年度', [column]: previousYearData[column], fill: 'url(#prevYear)'},
                {fiscal_year: currentYearData.fiscal_year + '年度', [column]: currentYearData[column], fill: 'url(#latestYear)'},
            ];
        } else {
            // 前年のデータがない場合
            data = [
                {fiscal_year: currentYearData.fiscal_year + '年度', [column]: currentYearData[column], fill: 'url(#latestYear)'},
            ];
        }
    } else {
        if (previousYearData) {
            data = [
                {fiscal_year: `${previousYearData.fiscal_year}/${previousYearData.type_of_period}`, [column]: previousYearData[column], fill: 'url(#prevYear)'},
                {fiscal_year: `${currentYearData.fiscal_year}/${currentYearData.type_of_period}`, [column]: currentYearData[column], fill: 'url(#latestYear)'},
            ];
        } else {
            data = [
                {fiscal_year: `${currentYearData.fiscal_year}/${currentYearData.type_of_period}`, [column]: currentYearData[column], fill: 'url(#latestYear)'},
            ];
        }
    }

    return (
        <div className={className}>
            <h4>{title}</h4>
            <ResponsiveContainer width="100%" height={200}>
                <BarChart 
                    data={data}
                    margin={{ top: 25, right: 10, left: 10, bottom: 20 }}
                    barGap={60}
                    barSize={80}
                >
                    {/* 定義したグラデーション */}
                    <defs>
                        <linearGradient id="prevYear" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="grey" />
                            <stop offset="100%" stopColor="lightgrey" />
                        </linearGradient>
                        <linearGradient id="latestYear" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgba(45, 186, 77, .5)" />
                            <stop offset="100%" stopColor="rgba(45, 186, 177, .5)" />
                        </linearGradient>
                    </defs>
                    <ReferenceLine y={0} stroke="lightgray" />
                    <XAxis dataKey="fiscal_year" />
                    <YAxis hide
                        domain={range(data.map((dt) => dt[column]))}
                    />
                    <Bar
                        dataKey={column}
                        radius={[5, 5, 0, 0]}
                        isAnimationActive={true}
                        animationBegin={0}
                        animationDuration={1000}
                        animationEasing="ease-in-out"
                        label={<CustomLabel />}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ComparingGraph;
